import React, { useRef, useEffect, useCallback, useState, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { useEventListener } from 'ahooks';
import { translate } from '@shakkerai/util-i18n';
import { searchModelList } from '@shakkerai/services';
import { Icon } from '@shakkerai/components';
import { isMainland } from '@shakkerai/pro-utils';
import { SD3_MODELS_FAMILY, presetCheckpointsMap, BASETYPE_VALUE_TO_LABEL } from '@shakkerai/pro-models';
import vipImg from '../assets/vip.png';
import './index.scss';
import { mc } from '@shakkerai/report';
import { libVipIcon } from '@shakkerai/pro-commercial';

const TEMP_DISABLEED = true;

const LoraEnhanceWindow = React.memo(({ baseType, loraList, generator, onApply, onClose, isFull }) => {
  const [key, setKey] = useState(0);
  const [position, setPosition] = useState(null);
  const { loraModels, checkpoint, presetBaseModelId } = generator.data;
  useEffect(() => {
    const ref = document.getElementById(`prompt-input-${generator.id}`);
    const { x, y } = ref.getBoundingClientRect();

    let realX = x;
    let realY = y;
    if (isFull) {
      if (loraList.length > 1) {
        realY = y - 227 - 16;
      } else {
        realY = y - 144 - 16;
      }
    } else {
      realX = x - 300;
      realY = y - 20;
    }

    const pos = {
      x: realX,
      y: realY,
    };
    setPosition(pos);
    setKey(Date.now());
  }, [generator.id, loraList.length, isFull]);

  const handleGlobalClick = useCallback(
    (e) => {
      if (
        Array.from(document.querySelectorAll('[data-trigger="prompt-loraEnhance"]')).some((ele) =>
          ele.contains(e.target),
        )
      )
        return;

      if (e.target.closest('[data-trigger="prompt-loraEnhance"]')) {
        return;
      }

      if (e.target.closest('.prompt-container')) {
        return;
      }

      onClose();
    },
    [onClose],
  );
  const handleClose = () => {
    onClose?.();
  };
  useEventListener('click', handleGlobalClick);

  const isLoraDisabled = (lora) => {
    if (checkpoint) {
      return lora.baseType !== checkpoint.baseType;
    }

    if (loraModels.length) {
      return loraModels[0].baseType?.toLowerCase() !== lora.baseType?.toLowerCase();
    }

    if (presetBaseModelId) {
      return (
        !presetCheckpointsMap[presetBaseModelId]?.versionDetail?.baseType?.toLowerCase() ===
        lora.baseType?.toLowerCase()
      );
    }

    return true;
  };

  const renderItem = useCallback(
    (item) => {
      const selected = loraModels.some((model) => model?.versionId === item?.versionId);
      return (
        <div className="flex justify-between w-full leading-[17px] mb-[16px] text-[12px]">
          <div className="relative w-[44px]">
            <img src={item?.imageUrl} className="w-[50px] rounded-[2px] h-[66px] rounded-[28px] object-cover" />
            {/* <span className="absolute block w-[100%] text-center rounded-bl-[4px] text-[#FFF] rounded-br-[4px] h-[16px] bg-[#555]/60 bottom-0">
                {item?.versionName || ''}
              </span> */}
          </div>
          <div className="flex flex-1 ml-[8px] justify-between items-center">
            <div>
              <p className="w-full max-w-[112px] whitespace-nowrap overflow-hidden text-ellipsis">{item?.name}</p>
              <p className="mt-[12px] flex gap-2 items-center">
                <span className="">{item?.baseType}</span>
                {!!item.vipUsed && (
                  <>
                    <div className="bg-[#EFEFEF] w-[1px] h-[10px]"></div>
                    <img src={isMainland ? libVipIcon : vipImg} className="w-[13px]" alt="" />
                  </>
                )}
              </p>
              {isLoraDisabled(item) && (
                <div className="flex flex-1 items-center text-[#FF7878]">
                  <div className="text-xs font-grotesk font-semibold leading-3">
                    {translate(`model_lora_unavailable_toast`, {
                      name: `${BASETYPE_VALUE_TO_LABEL[baseType] || baseType}`.replace(/\s/g, ''),
                    })}
                  </div>
                </div>
              )}
            </div>
            <div
              className={`w-[64px] h-[24px] text-center leading-[24px] rounded-[2px] border-[1px] ${
                selected ? 'text-[#ADADAD] bg-[#F5F5F5] border-none' : 'text-[#005848] bg-primary/10 border-primary'
              }`}
            >
              {selected ? translate('model_used_short') : translate('model_use_short')}
            </div>
          </div>
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFull],
  );

  const handleSelectLora = (lora) => {
    onApply(lora);
    mc('tool.model.suggestion.use', {
      type: 'lora',
      model_version_uuid: lora.uuid,
    });
  };

  useEffect(() => {
    if (loraList.length) {
      mc('tool.model.suggestion.show', {
        type: 'lora',
        model_version_list: loraList.map((item) => item.uuid),
      });
    }
  }, [loraList]);

  if (!position) {
    return null;
  }

  return createPortal(
    <Rnd key={key} default={position} disableDragging scale={1} className="z-[102]">
      <div
        data-trigger="prompt-loraEnhance"
        className={`${
          isFull ? 'w-[160px]' : 'w-[296px]'
        } w-[296px] absolute z-102 flex flex-col rounded-[2px] overflow-y-auto overflow-x-hidden items-stretch bg-white shadow-light rounded-md`}
      >
        <div className="px-[16px] py-[12px] border-b border-t-[2px] border-b-[#EFEFEF] border-t-primary flex items-center justify-between text-xs text-[#222]">
          <div className="flex items-center">
            <span className="text-sm font-normal text-[#222]">
              {isFull ? translate('lora_recommend') : translate('lora_recommend')}
            </span>
          </div>
          <div className="flex items-center">
            <Icon
              className="icon-border_close-1 ml-[6px] cursor-pointer"
              fontSize={18}
              onClick={handleClose}
              color="#999"
            />
          </div>
        </div>

        <div className="overflow-x-hidden overflow-y-hidden w-[100%] mt-[16px]">
          {loraList?.map((item, index) => (
            <div
              key={index}
              onClick={() => {
                handleSelectLora(item);
              }}
              className={`flex w-full items-baseline px-[16px] text-xs text-[#2D2D2D] rounded cursor-pointer `}
            >
              <div className={`flex w-full flex-1 flex-col leading-[14px] break-words break-all`}>
                {renderItem(item)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Rnd>,
    document.body,
  );
});

let prevRequestIndex = 0;

export const LoraEnhance = React.forwardRef((props, ref) => {
  const { onChange, generator, isFull } = props;
  const prevPrompt = useRef('');
  const [loraList, setLoraList] = useState([]);
  const [loading, setLoading] = useState(false);
  const { baseType, loraModels } = generator.data;

  const handleApply = useCallback(
    (value) => {
      setLoraList([]);
      onChange?.(value);
    },
    [onChange],
  );

  const parseData = (data) => {
    data = data || [];
    data = data.filter((item) => /lora/i.test(item?.modelType))?.slice(0, 2);
    try {
      data.forEach((item) => {
        const versionIntro = JSON.parse(item?.versionIntro || null);
        if (versionIntro) {
          item.versionIntro = versionIntro;
        }
      });
    } catch (error) {
      console.warn('parse feature failed', error);
    }
    return data;
  };

  const fetchLoraList = async (keyword, requestId) => {
    if (SD3_MODELS_FAMILY[baseType] || TEMP_DISABLEED) {
      return;
    }

    let newWords = '';

    if (prevPrompt.current.startsWith(keyword)) {
      prevPrompt.current = keyword;
      return;
    }

    if (keyword.startsWith(prevPrompt.current)) {
      newWords = keyword.replace(prevPrompt.current, '');
    } else {
      setLoraList([]);
      newWords = keyword;
    }

    prevPrompt.current = keyword;
    newWords = newWords.replace(/^[,，\s]+|[,，\s]+$/g, '');

    if (newWords) {
      setLoading(true);

      const currentRequestIndex = (prevRequestIndex = Date.now());

      const res = await searchModelList({
        genPrompt: newWords,
        requestId, //: uniqid(),
        baseTypes: [`${baseType || ''}`],
        modelType: 5,
      });

      // 如果请求过期，直接返回
      if (currentRequestIndex !== prevRequestIndex) {
        setLoading(false);
        return;
      }

      try {
        const list = parseData(res?.data?.data);
        const newList = list
          ?.filter((item) => !loraModels.some((model) => model?.versionId === item?.versionId))
          .slice(0, 2);

        setLoading(false);
        setLoraList((prevList) => newList.concat(prevList).slice(0, 2));

        return list.length > 0;
      } catch {
        // ...
      }
    }
  };

  const handleClose = useCallback(() => {
    setLoraList([]);
  }, []);

  useImperativeHandle(ref, () => ({
    fetchLoraList: fetchLoraList,
  }));

  if (loraList?.length <= 0 || TEMP_DISABLEED) {
    return null;
  }

  return (
    <LoraEnhanceWindow
      loading={loading}
      baseType={baseType}
      loraList={loraList}
      generator={generator}
      isFull={isFull}
      onApply={handleApply}
      onClose={handleClose}
    />
  );
});
